import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Avatar,
  Chip,
  Grid,
  Paper,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  IconButton,
  Tooltip,
  Button
} from '@mui/material';
import {
  Person as PersonIcon,
  SupervisorAccount as SupervisorIcon,
  Group as GroupIcon,
  Business as BusinessIcon,
  Work as WorkIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { API_URL } from '../../config/api';
import OrgTree from 'react-org-tree';

const AdminMyTeamTab = ({ user }) => {
  const { getAuthHeaders } = useAuth();
  const [teamData, setTeamData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (user?.id || user?._id) {
      fetchTeamData();
    } else {
      setLoading(false);
    }
  }, [user]);

  const fetchTeamData = async () => {
    try {
      setLoading(true);
      setError('');
      
      const userId = user?.id || user?._id;
      const response = await fetch(`${API_URL}/user-hierarchy/user-hierarchy/${userId}`, {
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Team data received:', data);
        setTeamData(data);
      } else {
        const errorText = await response.text();
        console.error('Failed to fetch hierarchy:', errorText);
        setError('Failed to load team information. Please try again.');
      }
    } catch (err) {
      console.error('Error fetching team data:', err);
      setError(`Failed to load team information: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Transform data for react-org-tree
  const transformDataForOrgTree = (data) => {
    if (!data || !data.nestedHierarchy) return null;
    
    const transformNode = (node) => ({
      id: node.userId,
      label: (
        <Box sx={{ textAlign: 'center', p: 1, minWidth: 150 }}>
          <Avatar sx={{ 
            width: 40, 
            height: 40, 
            mx: 'auto', 
            mb: 1, 
            bgcolor: node.role === 'Current User' ? '#1976d2' : 
                     node.role === 'Direct Report' ? '#4caf50' : 
                     node.role === 'Top Manager' ? '#e91e63' :
                     node.role === 'Direct Manager' ? '#9c27b0' :
                     node.role === 'Manager Level 2' ? '#673ab7' :
                     node.role === 'Manager Level 3' ? '#3f51b5' :
                     node.role === 'Manager Level 4' ? '#2196f3' : '#ff9800'
          }}>
            <PersonIcon />
          </Avatar>
          <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}>
            {node.name}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            {node.title || 'Employee'}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            {node.department || 'General'}
          </Typography>
          <Chip 
            label={node.role || 'Employee'} 
            size="small" 
            color={node.role === 'Current User' ? 'primary' : 
                   node.role === 'Direct Report' ? 'success' : 
                   node.role === 'Top Manager' ? 'error' :
                   node.role === 'Direct Manager' ? 'secondary' :
                   node.role === 'Manager Level 2' ? 'info' :
                   node.role === 'Manager Level 3' ? 'primary' :
                   node.role === 'Manager Level 4' ? 'primary' : 'warning'}
            sx={{ mt: 0.5, fontSize: '0.7rem' }}
          />
        </Box>
      ),
      children: node.children ? node.children.map(transformNode) : []
    });
    
    return transformNode(data.nestedHierarchy);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
        <Typography variant="body2" sx={{ ml: 2 }}>
          Loading team data...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
        <Box sx={{ mt: 2 }}>
          <Button variant="outlined" size="small" onClick={fetchTeamData}>
            Retry
          </Button>
        </Box>
      </Alert>
    );
  }

  if (!teamData) {
    return (
      <Alert severity="info" sx={{ m: 2 }}>
        No team data available.
      </Alert>
    );
  }

  const orgTreeData = transformDataForOrgTree(teamData);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" component="h2" gutterBottom color="primary" sx={{ mb: 3 }}>
        <WorkIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
        {user?.firstName} {user?.lastName}'s Team
      </Typography>

      <Grid container spacing={3}>
        {/* Current User Info */}
        <Grid item xs={12} md={4}>
          <Paper elevation={2} sx={{ p: 3, textAlign: 'center' }}>
            <Avatar 
              sx={{ 
                width: 80, 
                height: 80, 
                bgcolor: '#1976d2',
                mx: 'auto',
                mb: 2
              }}
            >
              <PersonIcon sx={{ fontSize: 40 }} />
            </Avatar>
            <Typography variant="h5" component="h3" gutterBottom>
              {teamData.user?.name || `${user?.firstName} ${user?.lastName}` || 'Unknown'}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {teamData.user?.email || user?.email || 'No email'}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {teamData.user?.title || 'Employee'} • {teamData.user?.department || 'General'}
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" color="primary">
                {teamData.directReportsCount || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Direct Reports
              </Typography>
            </Box>
          </Paper>
        </Grid>

        {/* Team Members List */}
        <Grid item xs={12} md={8}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
              <GroupIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Team Members ({teamData.directReportsCount || 0})
            </Typography>
            
            {teamData.teamMembers && teamData.teamMembers.length > 0 ? (
              <List>
                {teamData.teamMembers.map((member, index) => (
                  <React.Fragment key={`member-${member.userId}-${index}`}>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: '#4caf50' }}>
                          <PersonIcon />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={member.name}
                        secondary={
                          <Box>
                            <Typography variant="body2" component="span">
                              {member.email}
                            </Typography>
                            <Typography variant="body2" component="span" display="block">
                              {member.title || 'Employee'} • {member.department || 'General'}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                    {index < teamData.teamMembers.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            ) : (
              <Alert severity="info">
                No team members assigned yet.
              </Alert>
            )}
          </Paper>
        </Grid>

        {/* Managers Above */}
        {teamData.managersAbove && teamData.managersAbove.length > 0 && (
          <Grid item xs={12}>
            <Paper elevation={2} sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                <SupervisorIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Managers ({teamData.managersAbove.length})
              </Typography>
              <List>
                {teamData.managersAbove.map((manager, index) => (
                  <React.Fragment key={`manager-${manager.userId}-${index}`}>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: '#ff9800' }}>
                          <SupervisorIcon />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={manager.name}
                        secondary={
                          <Box>
                            <Typography variant="body2" component="span">
                              {manager.email}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                    {index < teamData.managersAbove.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </Paper>
          </Grid>
        )}

        {/* Organization Tree Visualization */}
        {orgTreeData && (
          <Grid item xs={12}>
            <Paper elevation={2} sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                <BusinessIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Organization Tree
              </Typography>
              <Box sx={{ 
                maxHeight: 600, 
                overflow: 'auto', 
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                p: 2,
                bgcolor: '#fafafa'
              }}>
                <OrgTree
                  data={orgTreeData}
                  collapsable={false}
                  expandAll={true}
                  horizontal={false}
                  nodeWidth={200}
                  nodeHeight={120}
                  nodeSpacing={50}
                  levelHeight={150}
                />
              </Box>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default AdminMyTeamTab;
